import { computed, ref } from 'vue';
import type { Errors, ModelSeries, Paint, Style } from '@dh-io-owpi/backend-api/src/data-contracts';
import type { ValidationNotification } from '@dh-io-owpi/shared/src/components/ErrorList/ValidationNotification';
import { defineStore } from '@dh-io-owpi/shared/src/stores/store';
import { useLoading } from '@dh-io-owpi/shared/src/composables/useLoading';
import { assertType } from '@dh-io-owpi/shared/src/utils/assertType';
import { useAemData } from '@/composables/useAemData';
import { getModelSeries } from '@dh-io-owpi/backend-api/src/Spa';

import { usePageStateStore } from './pageState';
import type { HttpResponse } from '@dh-io-owpi/backend-api/src/http-client';
import { useSectionsStore, sectionTrackLabelMap, type SectionName } from './sections';
import { useTrackingStore } from '@dh-io-owpi/shared/src/stores/tracking';

export const useStageSwitchersStore = defineStore('spa-stageSwitchers', () => {
  const aemData = useAemData();
  const pageState = usePageStateStore();
  const { load } = useLoading('stage');
  const sectionsStore = useSectionsStore();
  const { systemError } = useTrackingStore();
  const validationErrors = ref<ValidationNotification[]>([]);

  const styles = ref<Style[]>();
  const errorMessage = ref<string>();
  const dataRef = ref<ModelSeries>();
  const sections: SectionName[] = ['interior', 'exterior', 'offerTeaser'];

  async function fetchData() {
    const { country, language, modelSeries, vehicleColorCode } = aemData;
    try {
      const { data } = await getModelSeries({
        country,
        language,
        modelSeries,
        'pre-selected-paint-id': vehicleColorCode,
      });
      dataRef.value = data;
      styles.value = data.styles;
      pageState.setStyles(data.styles);
      sectionsStore.showSections(sections);

      validationErrors.value =
        data.errors?.map(({ key, aemAuthorMessage, severity }) => ({
          mode: severity == 'ERROR' ? 'error' : 'warning',
          message: `${key}: ${aemAuthorMessage}`,
        })) ?? [];
    } catch (e) {
      if (assertType<HttpResponse<ModelSeries, Errors>>(e, 'error'))
        errorMessage.value = e.error?.errors?.[0]?.message ?? e.error;
      else errorMessage.value = (e as Error).message;

      sectionsStore.hideSections(sections);
      const sectionsTrackLabel = sections.map((section) => sectionTrackLabelMap[section]);
      systemError({ action: 'pi_pages.sections', label: `load_fail.${sectionsTrackLabel.join(',')}` });
    }
  }

  const errors = computed<ValidationNotification[]>(() =>
    validationErrors.value?.length
      ? validationErrors.value
      : errorMessage.value
        ? [{ message: errorMessage.value, mode: 'error' }]
        : [],
  );

  load(fetchData());

  return {
    errors,
    styles,
    bm4: computed(() => dataRef.value?.bm4),
    bodyType: computed(() => dataRef.value?.bodyTypeId),
    brandId: computed(() => pageState.brandId),
    paints: computed<Paint[]>(() => pageState.style?.paints ?? []),
    selectedStyle: computed(() => pageState.style),
    selectedPaint: computed(() => pageState.paint),
    switchStyle: pageState.setStyle,
    switchPaint: pageState.setPaint,
  };
});
